import { Form } from 'formik';
import { assign, keys, pick } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  CloseFormikDialogResult,
  DialogProps,
  FormControls,
  FormikDialog,
  FormikTextField,
} from 'components';
import { TranslationNamespace } from 'i18n';
import { BankDto } from 'types';

type Values = BankDto;

type Props = DialogProps<CloseFormikDialogResult<Values>>;

export const BankDetailsDialog: React.FC<Props> = ({ open, data, onClose }) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.banks',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const title = useMemo(
    () =>
      data ? t('details_dialog.edit_title') : t('details_dialog.create_title'),
    [t, data],
  );

  const getInitialValues = useCallback(
    () => ({
      code: '',
      name: '',
    }),
    [],
  );

  const [initialValues, setInitialValues] = useState(getInitialValues());

  const validationSchema: Yup.ObjectSchema<Values> = useMemo(
    () =>
      Yup.object().shape({
        code: Yup.string().required(tCommon('errors.required')),
        name: Yup.string().required(tCommon('errors.required')),
      }),
    [tCommon],
  );

  useEffect(() => {
    const _initialValues = getInitialValues();
    setInitialValues(assign(_initialValues, pick(data, keys(_initialValues))));
  }, [data, getInitialValues]);

  return (
    <FormikDialog
      dialogProps={{
        open,
        title,
        onClose,
      }}
      formikProps={{
        initialValues,
        validateOnMount: true,
        validationSchema,
      }}
    >
      {() => (
        <Form>
          <FormControls>
            <FormikTextField label={t('fields.code')} name="code" required />
            <FormikTextField label={t('fields.name')} name="name" required />
          </FormControls>
        </Form>
      )}
    </FormikDialog>
  );
};
